import React, { useState } from "react";
import {
  ShieldCheck,
  AlertTriangle,
  HeartPulse,
  Activity,
  Moon,
  Zap,
  Download,
  Users,
  CheckCircle2,
  Info,
  Lock,
  FileText,
  Sparkles,
  ArrowUpRight,
  TrendingUp,
  SlidersHorizontal,
  ChevronRight,
  Printer
} from "lucide-react";
import { calcPopulationMetrics, buildCorporateExecutiveCsv, downloadCsv } from "./helpers";
import HealthQuadrantScatter from "./HealthQuadrantScatter";
import AdoptionFunnelCard from "./AdoptionFunnelCard";
import ComorbidityClusterCard from "./ComorbidityClusterCard";
import AgeGroupRiskCard from "./AgeGroupRiskCard";
import HealthRadarHexagon from "./HealthRadarHexagon";
import WidgetInfoTooltip from "./WidgetInfoTooltip";
import PrevalenceTableCard from "./PrevalenceTableCard";
import InterventionsSection from "./InterventionsSection";
import MacroHealthDistributionCard from "./MacroHealthDistributionCard";

export default function AnalyticsTab({
  client,
  employees = [],
  forceSimulated: controlledForceSimulated,
  onToggleSimulated,
}) {
  const [internalForceSimulated, setInternalForceSimulated] = useState(false);
  const isControlled = controlledForceSimulated !== undefined;
  const forceSimulated = isControlled ? controlledForceSimulated : internalForceSimulated;
  const setForceSimulated = isControlled ? onToggleSimulated : setInternalForceSimulated;

  const metrics = calcPopulationMetrics(employees, forceSimulated);

  const handleDownloadCsv = () => {
    const csvContent = buildCorporateExecutiveCsv(client?.name, metrics);
    const filename = `WE360_Informe_Ejecutivo_${(client?.name || "Empresa").replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.csv`;
    downloadCsv(filename, csvContent);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6" data-testid="analytics-tab-container">
      {/* ── Subheader & Executive Actions Bar ───────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
              <ShieldCheck size={12} />
              Datos Agregados & Anonimizados (SST / GDPR)
            </span>
            {metrics.isSimulated && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                <Sparkles size={12} />
                Cohorte Demostrativa Activa
              </span>
            )}
          </div>
          <h2 className="text-lg font-bold text-slate-800 mt-1">
            Centro de Comando Epidemiológico & Salud Ocupacional
          </h2>
          <p className="text-xs text-slate-500">
            Monitoreo preventivo de la fuerza laboral sin vulnerar el secreto médico individual.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Segmented control: Datos Reales vs Demo / Proyección */}
          <div className="inline-flex items-center p-0.5 bg-slate-100 rounded-xl border border-slate-200" data-testid="cohort-mode-segmented">
            <button
              type="button"
              onClick={() => setForceSimulated(false)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${
                !forceSimulated
                  ? "bg-white text-slate-800 shadow-sm font-semibold border border-slate-200/60"
                  : "text-slate-600 hover:text-slate-900 border border-transparent"
              }`}
              data-testid="toggle-real-mode-btn"
            >
              Datos Reales ({employees.length})
            </button>
            <button
              type="button"
              onClick={() => setForceSimulated(true)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${
                forceSimulated
                  ? "bg-amber-500 text-white shadow-sm font-semibold border border-amber-600"
                  : "text-slate-600 hover:text-slate-900 border border-transparent"
              }`}
              data-testid="toggle-demo-mode-btn"
            >
              <Sparkles size={12} />
              Demo / Proyección (35)
            </button>
          </div>

          <button
            type="button"
            onClick={handlePrint}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium bg-slate-50 text-slate-700 border border-slate-200 hover:bg-slate-100 transition-colors cursor-pointer"
            data-testid="print-executive-report-btn"
          >
            <Printer size={13} />
            Imprimir
          </button>

          <button
            type="button"
            onClick={handleDownloadCsv}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm transition-all cursor-pointer"
            data-testid="download-csv-btn"
          >
            <Download size={14} />
            Descargar Informe SST (CSV)
          </button>
        </div>
      </div>

      {/* ── Top 4 Bento KPI Cards ─────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Score de Bienestar Global (6 Dimensiones) */}
        <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm relative">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Índice de Salud Global
              </span>
              <WidgetInfoTooltip
                title="Índice de Salud Global Corporativo"
                align="left"
                what="Puntuación ponderada multicriterio (0 a 100) que sintetiza las 6 condiciones clínicas clave de la plantilla laboral."
                calculation="Ponderación validada por consensos científicos: Presión Arterial (20%) + Control Glicémico (20%) + Composición Corporal / WHtR (20%) + Perfil Lipídico (15%) + Esfera Emocional (15%) + Actividad Física (10%)."
                standard="Consenso Ponderado AHA Life's Essential 8, OMS Prevención de Enfermedades Crónicas & Directrices OIT C161."
                relevance="Permite al Directorio y al Comité SST evaluar la resiliencia biomédica de toda la fuerza laboral y seguir la evolución de la salud poblacional mes a mes."
              />
            </div>
            <div className="w-8 h-8 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center">
              <HeartPulse size={16} />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-extrabold text-slate-900 tracking-tight" data-testid="corporate-score">
              {metrics.corporateScore != null ? metrics.corporateScore : "—"}
            </span>
            <span className="text-xs text-slate-400 font-medium">/ 100</span>
          </div>
          <div className="mt-3">
            <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
              <div
                className="h-2 rounded-full transition-all duration-500"
                style={{
                  width: `${metrics.corporateScore != null ? metrics.corporateScore : 0}%`,
                  background:
                    metrics.corporateScore == null
                      ? "#94A3B8"
                      : metrics.corporateScore >= 80
                      ? "#10B981"
                      : metrics.corporateScore >= 65
                      ? "#F59E0B"
                      : "#EF4444",
                }}
              />
            </div>
            <p className="text-[11px] text-slate-500 mt-2 flex items-center gap-1">
              <span
                className="w-2 h-2 rounded-full inline-block"
                style={{
                  background:
                    metrics.corporateScore == null
                      ? "#94A3B8"
                      : metrics.corporateScore >= 80
                      ? "#10B981"
                      : metrics.corporateScore >= 65
                      ? "#F59E0B"
                      : "#EF4444",
                }}
              />
              {metrics.corporateScore == null
                ? "Sin evaluaciones clínicas registradas aún"
                : metrics.corporateScore >= 80
                ? "Colectivo con perfil saludable favorable"
                : metrics.corporateScore >= 65
                ? "Riesgo moderado: activar prevención"
                : "Atención prioritaria requerida por SST"}
            </p>
            <span className="block text-[10px] text-slate-400 mt-1">
              {metrics.corporateScore == null
                ? "Requiere al menos una evaluación para calcular índice"
                : "6 condiciones clínicas evaluadas"}
            </span>
          </div>
        </div>

        {/* Card 2: Cobertura & Participación en Tamizaje (5 Fuentes F1-F5) */}
        <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Participación en Tamizaje
              </span>
              <WidgetInfoTooltip
                title="Participación en Tamizaje & Monitoreo FaceScan"
                align="left"
                what="Porcentaje de la plantilla censada que ha completado las 5 fuentes iniciales de datos (F1 Somatometría, F2 Laboratorios, F3 Actividad Física, F4 Sueño, F5 Bienestar Psicosocial)."
                calculation="Tasa de completitud F1 a F5 sobre la plantilla total. La medición por FaceScan rPPG se monitorea de forma complementaria para evaluar la Variabilidad de la Frecuencia Cardíaca (VFC) y la sobrecarga autonómica."
                standard="Estándar Epidemiológico CDC/NIOSH & Guías de Medicina Ocupacional OIT C161 (Meta óptima ≥ 75%)."
                relevance="Una participación superior al 75-80% confiere validez epidemiológica a los reportes de SST y certificaciones corporativas."
              />
            </div>
            <div className="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
              <Users size={16} />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-extrabold text-slate-900 tracking-tight" data-testid="participation-rate">
              {metrics.screeningMetrics?.completedAll5Percent ?? metrics.participationRate}%
            </span>
            <span className="text-xs text-slate-400 font-medium">
              ({metrics.screeningMetrics?.completedAll5Count ?? metrics.participatingCount} de {metrics.totalEmployees})
            </span>
          </div>
          <div className="mt-3">
            <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
              <div
                className="h-2 rounded-full bg-blue-600 transition-all duration-500"
                style={{ width: `${Math.min(100, metrics.screeningMetrics?.completedAll5Percent ?? metrics.participationRate)}%` }}
              />
            </div>
            <div className="flex items-center justify-between text-[11px] text-slate-500 mt-2">
              <span>5 fuentes completadas</span>
              <div className="flex items-center gap-1">
                <span className="text-blue-600 font-semibold">
                  rPPG: {metrics.screeningMetrics?.faceScanPercent ?? 0}%
                </span>
                <WidgetInfoTooltip
                  title="Telemetría Facial Óptica (rPPG FaceScan)"
                  size="sm"
                  align="right"
                  what="Captura óptica de pulso hemodinámico y variabilidad cardíaca a través de la microcirculación cutánea por cámara."
                  calculation="Porcentaje de colaboradores con escaneo facial óptico completado."
                  standard="Fotopletismografía remota computacional & FaceHeart Vitals SDK."
                  relevance="Monitorea el estrés neurovegetativo sin contacto directo ni instrumental invasivo."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Card 3: Prevalencia Cardiometabólica (WHtR > 0.5) */}
        <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Grasa Visceral (WHtR &gt; 0.5)
              </span>
              <WidgetInfoTooltip
                title="Grasa Visceral y Relación Cintura/Altura"
                align="right"
                what="Proporción de colaboradores que presentan exceso de adiposidad abdominal profunda alrededor de órganos vitales."
                calculation="Relación Cintura-Estatura (WHtR): Circunferencia de cintura (cm) / Talla (cm). Umbral de riesgo clínico: > 0.50 (guías internacionales IDF y Ashwell 2012)."
                standard="International Diabetes Federation (IDF) & Ashwell et al. 2012 Consensus."
                relevance="Es el predictor clínico más temprano de resistencia a la insulina, hígado graso no alcohólico, dislipidemia e infarto en población activa."
              />
            </div>
            <div className="w-8 h-8 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
              <Activity size={16} />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-extrabold text-slate-900 tracking-tight" data-testid="whtr-elevated">
              {metrics.whtrMetrics.elevatedPercent}%
            </span>
            <span className="text-xs text-amber-600 font-semibold">
              Riesgo Metabólico
            </span>
          </div>
          <div className="mt-3">
            <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
              <div
                className="h-2 rounded-full bg-amber-500 transition-all duration-500"
                style={{ width: `${metrics.whtrMetrics.elevatedPercent}%` }}
              />
            </div>
            <p className="text-[11px] text-slate-500 mt-2">
              Cintura/Altura promedio: <strong>{metrics.whtrMetrics.average}</strong>
            </p>
          </div>
        </div>

        {/* Card 4: Esfera Emocional Cuádruple (Estrés, Ansiedad, Burnout, Bienestar) */}
        <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm" data-testid="bento-emotional-card">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Sobrecarga Emocional
              </span>
              <WidgetInfoTooltip
                title="Evaluación Cuádruple de la Esfera Emocional"
                align="right"
                what="Monitoreo multidimensional del estado mental y neurovegetativo desagregado en 4 pilares: Estrés biológico autonómico (rPPG), Ansiedad subjetiva, Burnout y Bienestar global."
                calculation="Correlación entre la Variabilidad de la Frecuencia Cardíaca (Índice de Tensión de Baevsky por rPPG) y los instrumentos clínicos validados del formulario F5 (Maslach Burnout Inventory y GAD)."
                standard="Baevsky RM Stress Index (Task Force ESC/NASPE 1996) • Maslach Burnout Inventory (OMS CIE-11 QD85) • GAD-7 Scale."
                relevance="Permite diferenciar oportunamente entre estrés agudo temporal y agotamiento laboral severo (burnout), facilitando intervenciones psicosociales antes de que deriven en ausentismo."
              />
            </div>
            <div className="w-8 h-8 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center">
              <Zap size={16} />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-extrabold text-slate-900 tracking-tight" data-testid="stress-high">
              {metrics.emotionalMetrics?.stressPercent ?? metrics.stressMetrics.highPercent}%
            </span>
            <span className="text-xs text-rose-600 font-semibold">
              Tensión Autonómica
            </span>
          </div>
          <div className="mt-3">
            <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
              <div
                className="h-2 rounded-full bg-rose-500 transition-all duration-500"
                style={{ width: `${metrics.emotionalMetrics?.stressPercent ?? metrics.stressMetrics.highPercent}%` }}
              />
            </div>
            <div className="grid grid-cols-3 gap-1 text-[10px] text-slate-500 mt-2 text-center">
              <div className="bg-slate-50 rounded py-0.5 border border-slate-100 flex flex-col items-center">
                <div className="flex items-center justify-center gap-1">
                  <span className="text-slate-400">Ansiedad</span>
                  <WidgetInfoTooltip
                    title="Sub-escala Ansiedad Subjetiva"
                    size="sm"
                    align="center"
                    what="Tamizaje de síntomas de inquietud psicomotora, tensión muscular y preocupación anticipatoria."
                    calculation="Puntuación estandarizada en escala GAD validada en formulario F5."
                    standard="Generalized Anxiety Disorder 7-item (GAD-7) Scale & OMS."
                    relevance="Detección temprana de interferencia cognitiva y presentismo laboral."
                  />
                </div>
                <strong className="text-slate-700">{metrics.emotionalMetrics?.anxietyPercent ?? 19}%</strong>
              </div>
              <div className="bg-slate-50 rounded py-0.5 border border-slate-100 flex flex-col items-center">
                <div className="flex items-center justify-center gap-1">
                  <span className="text-slate-400">Burnout</span>
                  <WidgetInfoTooltip
                    title="Sub-escala Síndrome de Burnout"
                    size="sm"
                    align="center"
                    what="Agotamiento emocional crónico ligado a sobrecarga laboral no gestionada."
                    calculation="Dimensiones de fatiga psíquica y despersonalización (MBI)."
                    standard="Maslach Burnout Inventory (MBI) & OMS CIE-11 Código QD85."
                    relevance="Principal causa de desmotivación, rotación de personal y bajas laborales."
                  />
                </div>
                <strong className="text-slate-700">{metrics.emotionalMetrics?.burnoutPercent ?? 15}%</strong>
              </div>
              <div className="bg-slate-50 rounded py-0.5 border border-slate-100 flex flex-col items-center">
                <div className="flex items-center justify-center gap-1">
                  <span className="text-slate-400">Bienestar</span>
                  <WidgetInfoTooltip
                    title="Sub-escala Bienestar Psicosocial"
                    size="sm"
                    align="right"
                    what="Percepción de vitalidad positiva, balance emocional y satisfacción laboral."
                    calculation="Índice de bienestar subjetivo (WHO-5 Well-Being Index)."
                    standard="Organización Mundial de la Salud (WHO-5 Psychological Well-being Index)."
                    relevance="Factor protector que modula la resiliencia del equipo ante alta exigencia."
                  />
                </div>
                <strong className="text-emerald-600">{metrics.emotionalMetrics?.wellbeingPercent ?? 66}%</strong>
              </div>
            </div>

            {/* Desglose Clínico MBI (3 Dimensiones Maslach: Agotamiento, Despersonalización y Falta de Realización) */}
            <div className="mt-2.5 pt-2 border-t border-slate-100">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                  Dimensiones MBI (Maslach)
                </span>
                <span className="text-[9px] text-slate-400 font-medium">OMS CIE-11 QD85</span>
              </div>
              <div className="grid grid-cols-3 gap-1 text-[9px]">
                <div className="bg-amber-50/60 rounded px-1.5 py-1 border border-amber-100 flex flex-col items-center text-center">
                  <span className="text-amber-800 font-medium leading-tight">Agotamiento</span>
                  <strong className="text-amber-900 font-bold text-[11px] mt-0.5">
                    {metrics.emotionalMetrics?.burnoutDimensions?.exhaustionPercent ?? 18}%
                  </strong>
                </div>
                <div className="bg-purple-50/60 rounded px-1.5 py-1 border border-purple-100 flex flex-col items-center text-center">
                  <span className="text-purple-800 font-medium leading-tight">Despersonaliz.</span>
                  <strong className="text-purple-900 font-bold text-[11px] mt-0.5">
                    {metrics.emotionalMetrics?.burnoutDimensions?.depersonalizationPercent ?? 14}%
                  </strong>
                </div>
                <div className="bg-rose-50/60 rounded px-1.5 py-1 border border-rose-100 flex flex-col items-center text-center">
                  <span className="text-rose-800 font-medium leading-tight">Baja Realiz.</span>
                  <strong className="text-rose-900 font-bold text-[11px] mt-0.5">
                    {metrics.emotionalMetrics?.burnoutDimensions?.accomplishmentPercent ?? 12}%
                  </strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Macro-Estratificación Tripartita de Salud Poblacional (Consenso Dr. Álvarez) ── */}
      <MacroHealthDistributionCard
        metrics={metrics}
        totalEmployees={metrics.totalEmployees}
      />

      {/* ── Tabulador de Prevalencia de Patologías & Concurrencia (Consensos Científicos) ─ */}
      <PrevalenceTableCard
        metrics={metrics}
        totalEmployees={metrics.totalEmployees}
      />

      {/* ── Embudo de Adopción & Cobertura de Tamizaje (Funnel SST) ───────── */}
      <AdoptionFunnelCard
        employees={employees}
        forceSimulated={forceSimulated}
      />

      {/* ── Triaje de Riesgo Poblacional (Niveles 0 a 3 Oficiales) ─────────────── */}
      <div className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-4">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold text-slate-900">
                Pirámide de Triaje de Riesgo Poblacional
              </h3>
              <span className="text-xs px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 font-mono">
                Multi-Agent HI-OS
              </span>
              <WidgetInfoTooltip
                title="Pirámide de Triaje Epidemiológico HI-OS"
                align="left"
                what="Estratificación escalonada de la fuerza laboral en 4 niveles de riesgo según la urgencia y tipo de acompañamiento de salud requerido."
                calculation="Motor de inferencia clínica HI-OS: Nivel 0 (Saludables sin banderas), Nivel 1 (1 factor leve / seguimiento IHO), Nivel 2 (Riesgo moderado / sugerencia consulta médico general), Nivel 3 (Alarma aguda prioritaria / cifras críticas o sospecha de patología aguda)."
                standard="Manchester Triage System (MTS) adaptado a Vigilancia Ocupacional & Protocolo OIT C161."
                relevance="Optimiza el gasto en salud corporativa, evitando sobrediagnósticos en personal sano y canalizando la atención médica presencial hacia quienes realmente lo necesitan."
              />
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Clasificación epidemiológica oficial de la plantilla en 4 niveles de intervención.
            </p>
          </div>
          <div className="text-xs text-slate-400">
            Muestra evaluada: <strong className="text-slate-700">{metrics.sampleSize}</strong> colaboradores
          </div>
        </div>

        {/* Stacked Proportional Visual Bar */}
        <div className="w-full h-3 rounded-full overflow-hidden flex mb-6 bg-slate-100">
          <div
            style={{ width: `${metrics.triaje.level0.percent}%`, background: metrics.triaje.level0.color }}
            title={`Nivel 0: ${metrics.triaje.level0.percent}%`}
          />
          <div
            style={{ width: `${metrics.triaje.level1.percent}%`, background: metrics.triaje.level1.color }}
            title={`Nivel 1: ${metrics.triaje.level1.percent}%`}
          />
          <div
            style={{ width: `${metrics.triaje.level2.percent}%`, background: metrics.triaje.level2.color }}
            title={`Nivel 2: ${metrics.triaje.level2.percent}%`}
          />
          <div
            style={{ width: `${metrics.triaje.level3.percent}%`, background: metrics.triaje.level3.color }}
            title={`Nivel 3: ${metrics.triaje.level3.percent}%`}
          />
        </div>

        {/* The 4 Triage Rows */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="triage-pyramid-grid">
          {/* Nivel 0 */}
          <div
            className="rounded-xl border p-4 flex flex-col justify-between"
            style={{ background: metrics.triaje.level0.bg, borderColor: "#A7F3D0" }}
          >
            <div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold px-2 py-0.5 rounded-md bg-white text-emerald-800 shadow-2xs">
                  {metrics.triaje.level0.badge}
                </span>
                <span className="text-lg font-black text-emerald-900">
                  {metrics.triaje.level0.percent}%
                </span>
              </div>
              <div className="flex items-center gap-1.5 mt-2">
                <h4 className="text-sm font-bold text-emerald-950">
                  {metrics.triaje.level0.label}
                </h4>
                <WidgetInfoTooltip
                  title="Triaje Nivel 0: Saludables / Resilientes"
                  size="sm"
                  align="left"
                  what="Colaboradores con todos sus biomarcadores clínicos en parámetros óptimos."
                  calculation="Ausencia de factores de riesgo mayores en somatometría, laboratorio o rPPG."
                  standard="AHA Life's Essential 8 & OMS Prevención Cuaternaria."
                  relevance="Blindaje del capital humano sano mediante retos de bienestar y pausas activas."
                />
              </div>
              <p className="text-xs text-emerald-800/90 mt-1 leading-relaxed">
                {metrics.triaje.level0.action}
              </p>
            </div>
            <div className="mt-3 pt-2 border-t border-emerald-200/60 flex items-center justify-between text-[11px] text-emerald-800 font-medium">
              <span>Plantilla estimada:</span>
              <strong>{metrics.triaje.level0.count} colaboradores</strong>
            </div>
          </div>

          {/* Nivel 1 */}
          <div
            className="rounded-xl border p-4 flex flex-col justify-between"
            style={{ background: metrics.triaje.level1.bg, borderColor: "#FDE68A" }}
          >
            <div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold px-2 py-0.5 rounded-md bg-white text-amber-800 shadow-2xs">
                  {metrics.triaje.level1.badge}
                </span>
                <span className="text-lg font-black text-amber-900">
                  {metrics.triaje.level1.percent}%
                </span>
              </div>
              <div className="flex items-center gap-1.5 mt-2">
                <h4 className="text-sm font-bold text-amber-950">
                  {metrics.triaje.level1.label}
                </h4>
                <WidgetInfoTooltip
                  title="Triaje Nivel 1: Riesgo Leve Incipiente"
                  size="sm"
                  align="left"
                  what="Presencia de un factor único leve o alteración reversible de hábitos."
                  calculation="1 factor borderline (ej. sobrepeso leve o estrés reactivo transitorio)."
                  standard="Guías OIT de Prevención Primaria en Centros de Trabajo."
                  relevance="Acompañamiento por Oficial de Salud (IHO) para corregir hábitos sin fármacos."
                />
              </div>
              <p className="text-xs text-amber-800/90 mt-1 leading-relaxed">
                {metrics.triaje.level1.action}
              </p>
            </div>
            <div className="mt-3 pt-2 border-t border-amber-200/60 flex items-center justify-between text-[11px] text-amber-800 font-medium">
              <span>Plantilla estimada:</span>
              <strong>{metrics.triaje.level1.count} colaboradores</strong>
            </div>
          </div>

          {/* Nivel 2 */}
          <div
            className="rounded-xl border p-4 flex flex-col justify-between"
            style={{ background: metrics.triaje.level2.bg, borderColor: "#FDBA74" }}
          >
            <div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold px-2 py-0.5 rounded-md bg-white text-orange-800 shadow-2xs">
                  {metrics.triaje.level2.badge}
                </span>
                <span className="text-lg font-black text-orange-900">
                  {metrics.triaje.level2.percent}%
                </span>
              </div>
              <div className="flex items-center gap-1.5 mt-2">
                <h4 className="text-sm font-bold text-orange-950">
                  {metrics.triaje.level2.label}
                </h4>
                <WidgetInfoTooltip
                  title="Triaje Nivel 2: Riesgo Moderado Concurrente"
                  size="sm"
                  align="left"
                  what="Co-existencia de dos o más factores de riesgo que ameritan valoración médica."
                  calculation="Presencia simultánea de 2 factores (ej. dislipidemia + tensión elevada)."
                  standard="Consenso OMS / ACC / ADA de Riesgo Cardiovascular Global."
                  relevance="Recomendación de consulta médica para plan terapéutico y preventivo individual."
                />
              </div>
              <p className="text-xs text-orange-800/90 mt-1 leading-relaxed">
                {metrics.triaje.level2.action}
              </p>
            </div>
            <div className="mt-3 pt-2 border-t border-orange-200/60 flex items-center justify-between text-[11px] text-orange-800 font-medium">
              <span>Plantilla estimada:</span>
              <strong>{metrics.triaje.level2.count} colaboradores</strong>
            </div>
          </div>

          {/* Nivel 3 */}
          <div
            className="rounded-xl border p-4 flex flex-col justify-between"
            style={{ background: metrics.triaje.level3.bg, borderColor: "#FECACA" }}
          >
            <div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold px-2 py-0.5 rounded-md bg-white text-rose-800 shadow-2xs">
                  {metrics.triaje.level3.badge}
                </span>
                <span className="text-lg font-black text-rose-900">
                  {metrics.triaje.level3.percent}%
                </span>
              </div>
              <div className="flex items-center gap-1.5 mt-2">
                <h4 className="text-sm font-bold text-rose-950">
                  {metrics.triaje.level3.label}
                </h4>
                <WidgetInfoTooltip
                  title="Triaje Nivel 3: Alarma Aguda Prioritaria"
                  size="sm"
                  align="left"
                  what="Valores críticos de bioseñales o descompensación evidente que exigen atención inmediata."
                  calculation="PAS ≥ 140 / PAD ≥ 90, glucemia descontrolada o arritmia severa detectada."
                  standard="Criterios de Crisis Hipertensiva y Descompensación Aguda ACC/AHA & ADA."
                  relevance="Derivación clínica prioritaria para prevenir infartos, crisis vasculares y accidentes laborales."
                />
              </div>
              <p className="text-xs text-rose-800/90 mt-1 leading-relaxed">
                {metrics.triaje.level3.action}
              </p>
            </div>
            <div className="mt-3 pt-2 border-t border-rose-200/60 flex items-center justify-between text-[11px] text-rose-800 font-medium">
              <span>Plantilla estimada:</span>
              <strong>{metrics.triaje.level3.count} colaboradores</strong>
            </div>
          </div>
        </div>
      </div>

      {/* ── Matriz de Dispersión Poblacional (4 Cuadrantes de Riesgo) ────────── */}
      <HealthQuadrantScatter
        employees={employees}
        forceSimulated={forceSimulated}
      />

      {/* ── Matriz de Carga de Comorbilidad & Clúster Multirriesgo ─────────── */}
      <ComorbidityClusterCard
        employees={employees}
        forceSimulated={forceSimulated}
      />

      {/* ── Segmentación por Grupos Etarios & Radar Holístico 360° ─────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <AgeGroupRiskCard
          employees={employees}
          forceSimulated={forceSimulated}
        />
        <HealthRadarHexagon
          employees={employees}
          forceSimulated={forceSimulated}
        />
      </div>

      {/* ── Matriz 2x2 de Factores de Riesgo Clínico & Ocupacional ───────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Card A: Composición Corporal e IMC */}
        <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-1.5">
                <h4 className="text-sm font-bold text-slate-900">Composición Corporal & IMC</h4>
                <WidgetInfoTooltip
                  title="Composición Corporal e Índice de Masa Corporal (IMC)"
                  align="left"
                  what="Distribución poblacional de peso corporal estandarizado respecto a la estatura según los umbrales de la Organización Mundial de la Salud (OMS)."
                  calculation="IMC = Peso (kg) / [Estatura (m)]². Normopeso: 18.5 a 24.9 | Sobrepeso: 25.0 a 29.9 | Obesidad: ≥ 30.0 kg/m²."
                  standard="Organización Mundial de la Salud (OMS 2000 Technical Report Series 894)."
                  relevance="Identifica el riesgo acumulado de fatiga osteomuscular, lumbalgias mecánicas, desórdenes músculo-esqueléticos y sobrecarga articular en puestos operativos y administrativos."
                />
              </div>
              <p className="text-xs text-slate-500">Distribución de peso e índice de masa corporal</p>
            </div>
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">
              Promedio: {metrics.imcMetrics.average}
            </span>
          </div>

          <div className="space-y-3">
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-600 font-medium">Normopeso (18.5 - 24.9)</span>
                <span className="font-bold text-emerald-700">{metrics.imcMetrics.normoPercent}%</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2">
                <div className="bg-emerald-500 h-2 rounded-full" style={{ width: `${metrics.imcMetrics.normoPercent}%` }} />
              </div>
            </div>

            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-600 font-medium">Sobrepeso (25.0 - 29.9)</span>
                <span className="font-bold text-amber-700">{metrics.imcMetrics.sobrepesoPercent}%</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2">
                <div className="bg-amber-500 h-2 rounded-full" style={{ width: `${metrics.imcMetrics.sobrepesoPercent}%` }} />
              </div>
            </div>

            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-600 font-medium">Obesidad (IMC ≥ 30)</span>
                <span className="font-bold text-rose-700">{metrics.imcMetrics.obesidadPercent}%</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2">
                <div className="bg-rose-500 h-2 rounded-full" style={{ width: `${metrics.imcMetrics.obesidadPercent}%` }} />
              </div>
            </div>
          </div>
        </div>

        {/* Card B: Perfil Hemodinámico (Presión Arterial) */}
        <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-1.5">
                <h4 className="text-sm font-bold text-slate-900">Perfil Hemodinámico (PA)</h4>
                <WidgetInfoTooltip
                  title="Perfil Hemodinámico y Presión Arterial"
                  align="left"
                  what="Distribución de cifras de presión arterial sistólica (PAS) y diastólica (PAD) en reposo según las guías clínicas internacionales ACC/AHA."
                  calculation="Normotensión (<120 y <80 mmHg) | Tensión Elevada (120-129 y <80 mmHg) | Cifras HTA (≥130 mmHg sistólica o ≥80 mmHg diastólica)."
                  standard="American College of Cardiology / American Heart Association (ACC/AHA 2017 Hypertension Guidelines)."
                  relevance="La hipertensión no diagnosticada es el principal detonante silente de emergencias cardiovasculares, infartos y accidentes cerebrovasculares en el trabajo."
                />
              </div>
              <p className="text-xs text-slate-500">Tensión arterial sistólica y diastólica</p>
            </div>
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">
              Media: {metrics.bpMetrics.avgPas}/{metrics.bpMetrics.avgPad} mmHg
            </span>
          </div>

          <div className="space-y-3">
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-600 font-medium">Normotensión (&lt;120 / &lt;80)</span>
                <span className="font-bold text-emerald-700">{metrics.bpMetrics.normalPercent}%</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2">
                <div className="bg-emerald-500 h-2 rounded-full" style={{ width: `${metrics.bpMetrics.normalPercent}%` }} />
              </div>
            </div>

            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-600 font-medium">Tensión Elevada (120-129 / &lt;80)</span>
                <span className="font-bold text-amber-700">{metrics.bpMetrics.elevatedPercent}%</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2">
                <div className="bg-amber-500 h-2 rounded-full" style={{ width: `${metrics.bpMetrics.elevatedPercent}%` }} />
              </div>
            </div>

            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-600 font-medium">Cifras HTA (≥130 o ≥80 mmHg)</span>
                <span className="font-bold text-rose-700">{metrics.bpMetrics.htaPercent}%</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2">
                <div className="bg-rose-500 h-2 rounded-full" style={{ width: `${metrics.bpMetrics.htaPercent}%` }} />
              </div>
            </div>
          </div>
        </div>

        {/* Card C: Actividad Física Ocupacional (IPAQ) */}
        <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-1.5">
                <h4 className="text-sm font-bold text-slate-900">Actividad Física & Sedentarismo</h4>
                <WidgetInfoTooltip
                  title="Actividad Física Ocupacional y Sedentarismo (IPAQ)"
                  align="left"
                  what="Evaluación del gasto energético semanal derivado del ejercicio y del tiempo promedio diario transcurrido en postura sedente continua."
                  calculation="Cuestionario Internacional de Actividad Física (IPAQ - Short Form): Sedentarios (<600 MET-min/sem) | Moderados (600-2999 MET-min/sem) | Vigorosos (≥3000 MET-min/sem)."
                  standard="OMS Directrices Mundiales sobre Actividad Física y Sedentarismo 2020 & IPAQ Research Committee."
                  relevance="Estar sentado más de 8 horas continuas incrementa el riesgo metabólico y de dolor lumbar, justificando pausas activas y mobiliario ergonómico."
                />
              </div>
              <p className="text-xs text-slate-500">Cuestionario Internacional de Actividad (IPAQ)</p>
            </div>
            <div className="w-7 h-7 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center">
              <TrendingUp size={15} />
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-600 font-medium">Sedentarios (&lt; 600 METs)</span>
                <span className="font-bold text-rose-700">{metrics.activityMetrics.sedentaryPercent}%</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2">
                <div className="bg-rose-500 h-2 rounded-full" style={{ width: `${metrics.activityMetrics.sedentaryPercent}%` }} />
              </div>
            </div>

            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-600 font-medium">Actividad Moderada (600-2999 METs)</span>
                <span className="font-bold text-amber-700">{metrics.activityMetrics.moderatePercent}%</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2">
                <div className="bg-amber-500 h-2 rounded-full" style={{ width: `${metrics.activityMetrics.moderatePercent}%` }} />
              </div>
            </div>

            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-600 font-medium">Vigorosos / Deportistas (≥3000 METs)</span>
                <span className="font-bold text-emerald-700">{metrics.activityMetrics.vigorousPercent}%</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2">
                <div className="bg-emerald-500 h-2 rounded-full" style={{ width: `${metrics.activityMetrics.vigorousPercent}%` }} />
              </div>
            </div>
          </div>
        </div>

        {/* Card D: Descanso y Sueño */}
        <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-1.5">
                <h4 className="text-sm font-bold text-slate-900">Higiene del Sueño & Descanso</h4>
                <WidgetInfoTooltip
                  title="Higiene del Sueño y Recuperación Biológica"
                  align="left"
                  what="Estimación de las horas continuas de descanso nocturno y detección de deuda biológica de sueño en el equipo."
                  calculation="Horas continuas reportadas en F1/F4. Umbral de Déficit Clínico: < 7.0 horas por noche (recomendación National Sleep Foundation)."
                  standard="National Sleep Foundation (NSF 2015 Consensus) & American Academy of Sleep Medicine (AASM)."
                  relevance="Dormir menos de 7 horas diarias disminuye la atención hasta un 32%, triplica la tasa de micro-sueños o accidentes en trayecto (in itinere) y eleva el cortisol."
                />
              </div>
              <p className="text-xs text-slate-500">Recuperación biológica y deuda de sueño</p>
            </div>
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">
              Media: {metrics.sleepMetrics.average} h / noche
            </span>
          </div>

          <div className="space-y-4">
            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/70 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                  <Moon size={16} />
                </div>
                <div>
                  <div className="text-xs font-bold text-slate-800">Déficit de Sueño (&lt; 7h)</div>
                  <div className="text-[11px] text-slate-500">Aumenta cortisol y riesgo de fatiga laboral</div>
                </div>
              </div>
              <span className="text-base font-extrabold text-indigo-900">
                {metrics.sleepMetrics.deficitPercent}%
              </span>
            </div>

            <p className="text-xs text-slate-500 leading-relaxed">
              El <strong>{100 - metrics.sleepMetrics.deficitPercent}%</strong> de los colaboradores descansa 7 o más horas, lo cual favorece la neuroplasticidad y la recuperación cardiovascular.
            </p>
          </div>
        </div>
      </div>

      {/* ── Matriz Estratégica de Intervenciones & Rutas de Atención (3 Niveles) ─ */}
      <InterventionsSection metrics={metrics} />

      {/* ── Protocolo de Privacidad y Cumplimiento Legal ─────────────────────── */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-5 text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center shrink-0 mt-0.5">
            <Lock size={18} className="text-teal-300" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-white flex items-center gap-2">
              Cumplimiento Estricto de Privacidad Médica & SST
            </h4>
            <p className="text-xs text-slate-300 mt-0.5 leading-relaxed max-w-2xl">
              Los datos presentados son consolidados poblacionales con propósitos de seguridad ocupacional y epidemiología laboral. Ningún dato clínico confidencial, diagnóstico o resultado de escaneo individual es visible para Recursos Humanos, garantizando el secreto médico profesional.
            </p>
          </div>
        </div>
        <div className="shrink-0 flex items-center gap-2">
          <span className="text-xs font-mono text-teal-300 px-3 py-1 rounded-lg bg-white/5 border border-white/10">
            GDPR / HIPAA Compliant
          </span>
        </div>
      </div>
    </div>
  );
}
